'use strict';

import extend from 'lodash/extend';

/**
 * Low-level communication handler for the View service.
 * Uses Q-based promises for all operations.
 */

(function($) {
  window.atex = window.atex || {};
  window.atex.aceui = window.atex.aceui || {};
  window.atex.aceui.service = window.atex.aceui.service || {};

  /**
   * Creates a new ViewService object.
   *
   * Argument 'viewServiceUrl' - The URL to the view service in the Content REST API.
   * Argument 'serviceSettings' - An object containing settings for this service.
   *
   * The 'serviceSettings' object should look like the following:
   *
   * {
   *   customQ: customQObject
   * }
   *
   * Property 'customQ' is an implementation of a promise based Q library,
   * i.e Angular $q. Defaults to a global Q.
   *
   * Returns a new ViewService object.
   */
  atex.aceui.service.ViewService = function(viewServiceUrl, serviceSettings) {
    var settings = extend({}, serviceSettings);
    var q = settings.customQ || window.Q;

    return {
      /**
       * Assigns a content version to a view.
       *
       * @param {Object} transferInfo - a transfer info object containing request details
       * @param {string} transferInfo.version - the version to assign to the view
       * @param {string} transferInfo.view - the view to assign the version to
       * @param {string} transferInfo.revision - the last known revision of the content
       * @param {string} transferInfo.authToken - the authorization token to use for the request
       *
       * @typedef {Object} AssignToViewResult - an object containing the result of view assign request
       * @property {string} revision - the new revision of the updated content
       *
       * @returns {Promise.<AssignToViewResult, atex.aceui.error.Error>}
       */
      assignToView: function(transferInfo) {
        if (!transferInfo.version) {
          var e = atex.aceui.error.Error({
            type: atex.aceui.error.types.INVALID_PARAMETERS,
            cause: "No version property present in transferInfo object",
            origin: "atex.aceui.service.ViewService"
          });

          return q.reject(e);
        }

        var deferred = q.defer();

        var fullUrl = viewServiceUrl + '/' + transferInfo.view;

        var dataObject = { version: transferInfo.version };

        $.ajax(fullUrl, {
          type: 'PUT',

          dataType: 'text', // NOTE: workaround since server returns 200 OK application/json but no data
          contentType: 'application/json',

          data: JSON.stringify(dataObject),

          beforeSend: function(jqXHR) {
            if (transferInfo.authToken !== undefined) {
              jqXHR.setRequestHeader("X-Auth-Token", transferInfo.authToken);
            }

            if (typeof transferInfo.revision !== 'undefined') {
              jqXHR.setRequestHeader("If-Match", transferInfo.revision);
            }
          },

          success: function(data, textStatus, jqXHR) {
            deferred.resolve({ revision: jqXHR.getResponseHeader('ETag') });
          },

          error: function(jqXHR, textStatus, errorThrown) {
            var e = atex.aceui.HTTPUtils.createError(jqXHR, 'atex.aceui.service.ViewService', {
              401: 'Could not authenticate user when assigning content "' + transferInfo.version + '" to view',
              403: 'Permission denied when assigning content "' + transferInfo.version + '" to view',
              409: 'Conflict with server version while assigning content "' + transferInfo.version + '" to view',
              'default': 'There was an error while assigning content "' + transferInfo.version + '" to view'
            });

            if (typeof e.rootCause !== 'undefined') {
              e.rootCause.textStatus = textStatus;
              e.rootCause.errorThrown = errorThrown;

              e.rootCause.transferInfo = transferInfo;
            }

            deferred.reject(e);
          }
        });

        return deferred.promise;
      },

      /**
       * Assigns a content version to a symbolic view.
       *
       * @param {Object} transferInfo - a transfer info object containing request details
       * @param {string} transferInfo.alias - the alias of the content to assign to the symbolic view
       * @param {string} transferInfo.view - the symbolic view to assign the version to
       * @param {string} transferInfo.targetView - the target view of the symbolic view
       * @param {string} transferInfo.revision - the last known revision of the content
       * @param {string} transferInfo.authToken - the authorization token to use for the request
       *
       * @typedef {Object} AssignToViewResult - an object containing the result of view assign request
       * @property {string} revision - the new revision of the updated content
       *
       * @returns {Promise.<AssignToViewResult, atex.aceui.error.Error>}
       */
      assignToSymbolicView: function(transferInfo) {
        if (!transferInfo.alias) {
          var e = atex.aceui.error.Error({
            type: atex.aceui.error.types.INVALID_PARAMETERS,
            cause: "No alias property present in transferInfo object",
            origin: "atex.aceui.service.ViewService"
          });

          return q.reject(e);
        }

        var deferred = q.defer();

        var fullUrl = viewServiceUrl + '/' + transferInfo.view;

        var dataObject = {
          symbolic: {
            alias: transferInfo.alias,
            targetView: '@' + transferInfo.targetView
          }
        };

        $.ajax(fullUrl, {
          type: 'PUT',

          dataType: 'text', // NOTE: workaround since server returns 200 OK application/json but no data
          contentType: 'application/json',

          data: JSON.stringify(dataObject),

          beforeSend: function(jqXHR) {
            if (transferInfo.authToken !== undefined) {
              jqXHR.setRequestHeader("X-Auth-Token", transferInfo.authToken);
            }

            if (typeof transferInfo.revision !== 'undefined') {
              jqXHR.setRequestHeader("If-Match", transferInfo.revision);
            }
          },

          success: function(data, textStatus, jqXHR) {
            deferred.resolve({ revision: jqXHR.getResponseHeader('ETag') });
          },

          error: function(jqXHR, textStatus, errorThrown) {
            var e = atex.aceui.HTTPUtils.createError(jqXHR, 'atex.aceui.service.ViewService', {
              401: 'Could not authenticate user when assigning content "' + transferInfo.alias + '" to symbolic view',
              403: 'Permission denied when assigning content "' + transferInfo.alias + '" to symbolic view',
              409: 'Conflict with server version while assigning content "' + transferInfo.alias + '" to symbolic view',
              'default': 'There was an error while assigning content "' + transferInfo.alias + '" to symbolic view'
            });

            if (typeof e.rootCause !== 'undefined') {
              e.rootCause.textStatus = textStatus;
              e.rootCause.errorThrown = errorThrown;

              e.rootCause.transferInfo = transferInfo;
            }

            deferred.reject(e);
          }
        });

        return deferred.promise;
      },

      /**
       * Unassigns a content from a view.
       *
       * @param {Object} transferInfo - a transfer info object containing request details
       * @param {string} transferInfo.alias - the alias of the content to unassign from the view
       * @param {string} transferInfo.view - the view to unassign the content from
       * @param {string} transferInfo.revision - the last known revision of the content
       * @param {string} transferInfo.authToken - the authorization token to use for the request
       *
       * @returns {Promise.<{Object}, atex.aceui.error.Error>}
       */
      unassignFromView: function(transferInfo) {
        if (!transferInfo.alias) {
          var e = atex.aceui.error.Error({
            type: atex.aceui.error.types.INVALID_PARAMETERS,
            cause: "No alias property present in transferInfo object",
            origin: "atex.aceui.service.ViewService"
          });

          return q.reject(e);
        }

        var deferred = q.defer();

        var fullUrl = viewServiceUrl + '/' + transferInfo.view + '/alias/' + transferInfo.alias;

        $.ajax(fullUrl, {
          type: 'DELETE',
          dataType: 'text', // NOTE: workaround since server returns 200 OK application/json but no data

          beforeSend: function(jqXHR) {
            if (transferInfo.authToken !== undefined) {
              jqXHR.setRequestHeader("X-Auth-Token", transferInfo.authToken);
            }

            if (typeof transferInfo.revision !== 'undefined') {
              jqXHR.setRequestHeader("If-Match", transferInfo.revision);
            }
          },

          success: function(data, textStatus, jqXHR) {
            deferred.resolve({ revision: jqXHR.getResponseHeader('ETag') });
          },

          error: function(jqXHR, textStatus, errorThrown) {
            var e = atex.aceui.HTTPUtils.createError(jqXHR, 'atex.aceui.service.ViewService', {
              401: 'Could not authenticate user when removing content "' + transferInfo.alias + '" from view',
              403: 'Permission denied when removing content "' + transferInfo.alias + '" from view',
              409: 'Conflict with server version while removing content "' + transferInfo.alias + '" from view',
              'default': 'There was an error while removing content "' + transferInfo.alias + '" from view'
            });

            if (typeof e.rootCause !== 'undefined') {
              e.rootCause.textStatus = textStatus;
              e.rootCause.errorThrown = errorThrown;

              e.rootCause.transferInfo = transferInfo;
            }

            deferred.reject(e);
          }
        });

        return deferred.promise;
      }
    };
  };
})(jQuery);
